import type { BudgetCommand, BudgetCommandResult, BudgetDocument } from '@/engines/budget'

import type { CommercialItem, CommitCommercialOptions } from '../types'
import { commercialItemToBudgetLines } from './commercialToBudget'

/** Minimal target: BudgetEngine or BudgetEditorAdapter-compatible. */
export interface CommercialBudgetTarget {
  dispatch: (command: BudgetCommand) => BudgetCommandResult
  peek?: () => BudgetDocument | null
  ensureDocument?: (title?: string) => unknown
}

/**
 * Commits CommercialItem(s) into the Budget Engine via Commands only.
 */
export class CommercialBudgetService {
  /**
   * Add one CommercialItem as Budget line(s).
   * Returns the last successful command result (document state).
   */
  addItem(
    target: CommercialBudgetTarget,
    item: CommercialItem,
    options?: CommitCommercialOptions,
  ): BudgetCommandResult {
    ensureBudget(target)
    const lines = commercialItemToBudgetLines(item, options)

    let last: BudgetCommandResult | null = null
    for (const line of lines) {
      last = target.dispatch({ type: 'AddLine', line })
      if (!last.ok) {
        throw Object.assign(new Error(last.error?.message ?? 'Falha ao adicionar item comercial'), {
          code: last.error?.code ?? 'ADD_COMMERCIAL_FAILED',
          result: last,
        })
      }
    }
    return last!
  }

  addItems(
    target: CommercialBudgetTarget,
    items: CommercialItem[],
    options?: CommitCommercialOptions,
  ): BudgetCommandResult {
    let last: BudgetCommandResult | null = null
    for (const item of items) {
      last = this.addItem(target, item, options)
    }
    if (!last) {
      throw Object.assign(new Error('Nenhum item comercial para adicionar'), {
        code: 'EMPTY_ITEMS',
      })
    }
    return last
  }
}

function ensureBudget(target: CommercialBudgetTarget): void {
  if (typeof target.ensureDocument === 'function') {
    target.ensureDocument()
    return
  }
  if (typeof target.peek === 'function' && !target.peek()) {
    target.dispatch({ type: 'CreateBudget', title: 'Orçamento Comercial' })
  }
}

export const commercialBudgetService = new CommercialBudgetService()
